<template>
  <div class="municipal-organization-container">
    <!-- ページヘッダー -->
    <div class="page-header mb-4">
      <div class="header-content">
        <h4 class="mb-0">
          <i class="bi bi-building me-2 text-primary"></i>自治体組織設定
        </h4>
        <BaseButton
          variant="secondary"
          size="sm"
          @click="handleBackToMenu"
        >
          <i class="bi bi-arrow-left me-2"></i>メニューに戻る
        </BaseButton>
      </div>
    </div>

    <!-- データテーブル -->
    <InfoTableSection
      title=""
      :headers="['自治体組織名', '操作']"
      :items="organizations"
      :loading="loading"
      :error="error"
      empty-message="まだ組織が登録されていません。下の「新しい組織を追加」ボタンから最初の組織を登録してください。"
      :pagination="{ enabled: true, perPage: 10 }"
      @action="(payload) => emit('table-action', payload)"
    >
      <template #tableRow="{ item }">
        <BTd class="organization-name">
          <ContentText color="default">{{ item.organizationName }}</ContentText>
        </BTd>
        <BTd class="text-center">
          <BaseButton
            size="sm"
            variant="success"
            class="me-2"
            @click="emit('show-edit-modal', item)"
          >
            <i class="bi bi-pencil-square me-1"></i>編集
          </BaseButton>
          <BaseButton
            size="sm"
            variant="danger"
            @click="emit('confirm-delete', item)"
          >
            <i class="bi bi-trash me-1"></i>削除
          </BaseButton>
        </BTd>
      </template>
    </InfoTableSection>

    <!-- 新規追加ボタン（常に表示） -->
    <div class="action-section mt-4">
      <div class="action-content">
        <BaseButton
          variant="primary"
          size="md"
          @click="handleShowAddModal"
          class="add-organization-btn"
          :disabled="loading"
        >
          <i class="bi bi-plus-circle me-2"></i>新しい組織を追加
        </BaseButton>

        <!-- 0件表示の場合のガイドメッセージ -->
        <div v-if="organizations.length === 0 && !loading && !error" class="mt-2">
          <small class="text-muted">
            <i class="bi bi-info-circle me-1"></i>
            組織を登録して廃棄物収集の管理を始めましょう
          </small>
        </div>

      </div>
    </div>

    <!-- 新規追加モーダル -->
    <BaseModal
      modal-id="add-organization-modal"
      :model-value="showAddModal"
      @update:model-value="val => !val && $emit('close-add-modal')"
      title="新しい組織を追加"
      confirm-text="追加"
      cancel-text="キャンセル"
      @confirm="handleAddSubmit"
      @cancel="handleAddCancel"
    >
      <OrganizationForm
        ref="addForm"
        :model-value="formData"
        :errors="formErrors"
        :loading="loading"
        :existing-organizations="organizations"
        @update:model-value="$emit('update:form-data', $event)"
      />
    </BaseModal>

    <!-- 編集モーダル -->
    <BaseModal
      modal-id="edit-organization-modal"
      :model-value="showEditModal"
      @update:model-value="val => !val && $emit('close-edit-modal')"
      title="組織情報を編集"
      confirm-text="更新"
      cancel-text="キャンセル"
      @confirm="handleEditSubmit"
      @cancel="handleEditCancel"
    >
      <OrganizationForm
        ref="editForm"
        :model-value="formData"
        :errors="formErrors"
        :loading="loading"
        :existing-organizations="organizations"
        @update:model-value="$emit('update:form-data', $event)"
      />
    </BaseModal>

    <!-- 削除確認モーダル -->
    <BaseModal
      modal-id="delete-organization-modal"
      :model-value="showDeleteModal"
      @update:model-value="val => !val && $emit('close-delete-modal')"
      title="削除の確認"
      confirm-text="削除"
      cancel-text="キャンセル"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    >
      <p>以下の組織を削除してもよろしいですか？</p>
      <div v-if="deleteTarget" class="alert alert-warning">
        <strong>{{ deleteTarget.organizationName }}</strong>
      </div>
      <p class="text-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        この操作は取り消すことができません。
      </p>
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import InfoTableSection from '@/components/organisms/InfoTableSection.vue'
import ContentText from '@/components/atoms/ContentText.vue'
import OrganizationForm from './OrganizationForm.vue'

/**
 * Props定義
 */
const props = defineProps({
  organizations: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  showAddModal: {
    type: Boolean,
    default: false
  },
  showEditModal: {
    type: Boolean,
    default: false
  },
  showDeleteModal: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({
      organizationName: ''
    })
  },
  formErrors: {
    type: Object,
    default: () => ({})
  },
  deleteTarget: {
    type: Object,
    default: null
  }
})

/**
 * Emits定義
 */
const emit = defineEmits([
  'show-add-modal',
  'show-edit-modal',
  'confirm-delete',
  'add-organization',
  'edit-organization',
  'delete-organization',
  'close-add-modal',
  'close-edit-modal',
  'close-delete-modal',
  'back-to-menu',
  'table-action'
])

// Refs for form components
const addForm = ref(null)
const editForm = ref(null)

/**
 * モーダル表示のemitハンドラー
 */
const handleShowAddModal = () => {
  emit('show-add-modal')
}


/**
 * 新規追加の送信処理
 */
const handleAddSubmit = () => {
  emit('add-organization', { ...props.formData })
}

/**
 * 新規追加のキャンセル処理
 */
const handleAddCancel = () => {
  emit('close-add-modal')
}

/**
 * 編集の送信処理
 */
const handleEditSubmit = () => {
  emit('edit-organization', { ...props.formData })
}

/**
 * 編集のキャンセル処理
 */
const handleEditCancel = () => {
  emit('close-edit-modal')
}

/**
 * 削除確認の送信処理
 */
const handleDeleteConfirm = () => {
  if (props.deleteTarget) {
    emit('delete-organization', props.deleteTarget)
  }
}

/**
 * 削除確認のキャンセル処理
 */
const handleDeleteCancel = () => {
  emit('close-delete-modal')
}

/**
 * メニューに戻る処理
 */
const handleBackToMenu = () => {
  emit('back-to-menu')
}


</script>

<style lang="scss" scoped>
.municipal-organization-container {
  padding: 1.5rem 0;
}

.page-header {
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 2rem;
    margin-right: 2rem;
  }

  h4 {
    color: #1f2937;
    font-weight: 600;
    flex: 1;
  }

  // レスポンシブ対応
  @media (max-width: 768px) {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
    }

    h4 {
      order: 1; // モバイルではタイトルを上に
    }

    button {
      order: 2; // ボタンを下に
      align-self: flex-end; // 右寄せ維持
    }
  }
}.organization-name {
  font-weight: 600;
  color: #374151;
}

/* ボタンアニメーション */
.btn-outline-primary:hover,
.btn-outline-danger:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.btn-outline-primary:hover {
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-outline-danger:hover {
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.action-section {
  padding: 1.5rem 0;

  .action-content {
    margin-left: 2rem;
    margin-right: 2rem;
    display: flex;
    justify-content: flex-start;
  }

  .add-organization-btn {
    padding: 0.75rem 2rem;
    font-weight: 600;
    border-radius: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    }
  }
}

// 戻るボタンのスタイリング
.page-header button {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

@media (max-width: 768px) {
  .action-section {
    padding: 1rem 0;

    .action-content {
      margin-left: 1rem;
      margin-right: 1rem;
    }

    .add-organization-btn {
      width: auto;
      min-width: 200px;
      max-width: 300px;
    }
  }
}
</style>
